import type { SnapshotLevel, SnapshotRow } from '@cortex/styling-pattern-watch-core';
import type { DashboardPayload } from './dashboard-types.js';

export interface ListSnapshotsResponse {
  rows: SnapshotRow[];
  count: number;
}

export interface ListSnapshotsArgs {
  fromIso?: string;
  toIso?: string;
  segmentName?: string;
  level?: SnapshotLevel;
  limit?: number;
}

export interface FetchSnapshotsInit {
  baseUrl: string;
  fetchImpl?: typeof fetch;
  authRedirect?: (url: string) => void;
  currentUrl?: string;
}

export interface FetchDashboardInit {
  baseUrl: string;
  fetchImpl?: typeof fetch;
  authRedirect?: (url: string) => void;
  currentUrl?: string;
}

/**
 * @graph-stack styling-pattern-watch
 * @graph-domain Styling
 * @graph-business styling-pattern-watch-api の GET /v1/dashboard を呼んでダッシュボード描画用のリッチデータを取得する fetch 関数。baseUrl は呼び出し側で必ず指定する。401 + X-Edge-Auth-Start 受信時は OAuth start へリダイレクトする
 * @graph-connects styling-pattern-watch-api [calls] /v1/dashboard を呼ぶ
 */
export async function fetchDashboard(init: FetchDashboardInit): Promise<DashboardPayload> {
  const fetchImpl = init.fetchImpl ?? fetch;
  const baseUrl = init.baseUrl.replace(/\/$/, '');
  const url = `${baseUrl}/v1/dashboard`;
  const res = await fetchImpl(url, { credentials: 'include' });
  if (!res.ok) {
    if (res.status === 401 && redirectToAuthStart(res.headers.get('X-Edge-Auth-Start'), init)) {
      throw new Error('Authentication required');
    }
    throw new Error(`API error ${String(res.status)}`);
  }
  return (await res.json()) as DashboardPayload;
}

/**
 * @graph-stack styling-pattern-watch
 * @graph-domain Styling
 * @graph-business styling-pattern-watch-api の GET /v1/snapshots を呼んでスナップショット履歴を取得する fetch 関数。baseUrl は呼び出し側で必ず指定する（同一オリジンへのサイレントフォールバックは禁止）。Edge Auth Cookie をブラウザから送出するため credentials: 'include' を指定し、401 + X-Edge-Auth-Start 受信時は OAuth start へリダイレクトする
 * @graph-connects styling-pattern-watch-api [calls] /v1/snapshots を呼ぶ
 */
export async function fetchSnapshots(
  args: ListSnapshotsArgs,
  init: FetchSnapshotsInit,
): Promise<ListSnapshotsResponse> {
  const fetchImpl = init.fetchImpl ?? fetch;
  const baseUrl = init.baseUrl.replace(/\/$/, '');
  const params = new URLSearchParams();
  if (args.fromIso !== undefined) params.set('from', args.fromIso);
  if (args.toIso !== undefined) params.set('to', args.toIso);
  if (args.segmentName !== undefined) params.set('segment', args.segmentName);
  if (args.level !== undefined) params.set('level', args.level);
  if (args.limit !== undefined) params.set('limit', String(args.limit));

  const url = `${baseUrl}/v1/snapshots${params.size > 0 ? `?${params.toString()}` : ''}`;
  const res = await fetchImpl(url, { credentials: 'include' });
  if (!res.ok) {
    if (res.status === 401 && redirectToAuthStart(res.headers.get('X-Edge-Auth-Start'), init)) {
      throw new Error('Authentication required');
    }
    throw new Error(`API error ${String(res.status)}`);
  }
  const data = (await res.json()) as ListSnapshotsResponse;
  return data;
}

/**
 * @graph-connects none
 * @graph-business 401 レスポンスの X-Edge-Auth-Start ヘッダを読み取り、auth.air-closet.ai の OAuth start URL だけに return パラメータ付きでリダイレクトして認証フローを起動する。不正 URL・未信頼ホスト・リダイレクト不可なら false を返す
 */
function redirectToAuthStart(startUrl: string | null, init: FetchSnapshotsInit): boolean {
  if (!startUrl) return false;
  const returnUrl = init.currentUrl ?? getCurrentBrowserUrl();
  if (!returnUrl) return false;
  let authUrl: URL;
  try {
    authUrl = new URL(startUrl);
  } catch (error) {
    // 不正 URL は TypeError として失敗。他は予期しないので再 throw
    if (!(error instanceof TypeError)) {
      throw error;
    }
    return false;
  }
  if (authUrl.hostname !== 'auth.air-closet.ai') return false;
  authUrl.searchParams.set('return', returnUrl);
  const redirectUrl = authUrl.toString();
  if (init.authRedirect) {
    init.authRedirect(redirectUrl);
    return true;
  }
  if (typeof window === 'undefined') return false;
  window.location.assign(redirectUrl);
  return true;
}

/**
 * @graph-connects none
 * @graph-business ブラウザ環境で現在のページ URL を取得する。SSR や window 未定義環境では null を返す
 */
function getCurrentBrowserUrl(): string | null {
  if (typeof window === 'undefined') return null;
  return window.location.href;
}
